import {CertificateHeader} from "../../template/Certificate/Header";
import {CertificateColumn} from "../../template/Certificate/Column";
import {CertificateBody} from "../../template/Certificate/Body";
import {CertificatePage} from "../../template/Certificate/Page";
import {RealizationCertificate} from "../../model";
import {ActorTransformationCard} from "../Actor/ActorTransformationCard";
import {OuvrageRefsSection} from "./OuvrageRefsSection";
import { SocialsSection } from "../socials/SocialsSection";
import { CertificateBlock } from "App/template/Certificate/Block";
import { RealizationRefsSection } from "./RealizationRefsSection";
import { ImageSection } from "./ImageSection";
import { RemovalZoneSection } from "./RemovalZoneSection";
import { RealizationCertificationLinkCertificateSection } from "./RealizationCertificationLinkCertificateSection";
import { DescriptionSection } from "./DescriptionSection";
import { RealizationDetailSection } from "./RealizationDetailSection";
import { IndicatorSection } from "./IndicatorSection";
import { MapSection } from "./MapSection";
import { RemovalZone } from "@tracabois/domain";
import type { org } from "boislocal-platform-platform-api-api-js-export";
import { Feature, FeatureCollection, Point, Position, LineString, MultiLineString } from "geojson";
import { LatLngBounds, LatLngLiteral } from "leaflet";
import L from "leaflet";

type GeoCoordinates = org.boislocal.platform.s2.commons.domain.model.GeoCoordinatesDTO;

const buildMapGeoJson = (removalZones: RemovalZone[], realizationCoordinates?: GeoCoordinates): FeatureCollection => {
  const features: Feature[] = [];

  // Add realization location marker
  if (realizationCoordinates) {
    features.push({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [realizationCoordinates.longitude, realizationCoordinates.latitude] },
      properties: { type: "realization" },
    });
  }

  // Add removal zone features
  removalZones.forEach((zone) => {
    if (zone.geoJson?.features?.length) {
      features.push(...(zone.geoJson.features as Feature[]).map((f) => ({
        ...f,
        properties: { ...f.properties, type: "removalZone" },
      })));
    } else if (zone.geo) {
      features.push({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [zone.geo.longitude, zone.geo.latitude] },
        properties: { radius: zone.radius ?? 10000, type: "removalZone" },
      });
    }
  });

  return { type: "FeatureCollection", features };
};

const computeBounds = (geoJson: FeatureCollection): LatLngBounds | undefined => {
  const coordinates: LatLngLiteral[] = [];

  geoJson.features.forEach((feature: Feature) => {
    switch (feature.geometry.type) {
      case "Point": {
        const lng = (feature.geometry as Point).coordinates[0];
        const lat = (feature.geometry as Point).coordinates[1];
        const radiusM = (feature.properties as any)?.radius;
        if (radiusM && radiusM > 0) {
          // Pad the point bounds by the radius (approximate degrees from meters)
          const latDelta = radiusM / 111320;
          const lngDelta = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
          coordinates.push({ lng: lng - lngDelta, lat: lat - latDelta });
          coordinates.push({ lng: lng + lngDelta, lat: lat + latDelta });
        } else {
          coordinates.push({ lng, lat });
        }
        break;
      }
      case "LineString":
      case "MultiPoint":
        (feature.geometry as LineString).coordinates.forEach((position: Position) => {
          coordinates.push({ lng: position[0], lat: position[1] });
        });
        break;
      case "MultiLineString":
      case "Polygon":
        (feature.geometry as MultiLineString).coordinates.forEach((positions: Position[]) => {
          positions.forEach((position: Position) => {
            coordinates.push({ lng: position[0], lat: position[1] });
          });
        });
        break;
      case "MultiPolygon":
        (feature.geometry as any).coordinates.forEach((polygons: Position[][]) => {
          polygons.forEach((positions: Position[]) => {
            positions.forEach((position: Position) => {
              coordinates.push({ lng: position[0], lat: position[1] });
            });
          });
        });
        break;
      default:
        break;
    }
  });

  if (coordinates.length === 0) return undefined;

  const lngs = coordinates.map((c) => c.lng).sort((a, b) => a - b);
  const lats = coordinates.map((c) => c.lat).sort((a, b) => a - b);

  return L.latLngBounds(
    L.latLng(lats[0], lngs[0]),
    L.latLng(lats[lats.length - 1], lngs[lngs.length - 1]),
  );
};

export interface RealizationCertificateProps {
  item: RealizationCertificate
}

export const RealizationCertificateSection = (props: RealizationCertificateProps) => {
  const {item} = props;

  const hasRightColumn =
    (item?.removalZones && item.removalZones.length > 0) ||
    (item?.certifications && item.certifications.length > 0);

  const mapGeoJson = buildMapGeoJson(item.removalZones ?? [], item.coordinates ?? undefined);
  const mapBounds = computeBounds(mapGeoJson);

  return (
    <CertificatePage>
      <CertificateHeader title={item.name} certificateType="Réalisation"/>
      <CertificateBody image={item.images[0]}>
        <CertificateBlock withVerticalPadding withHorizontalMargin withBottomMargin withBorderRadius withBoxShadow>
          <CertificateColumn>
            <IndicatorSection indicators={item.indicators}/>
            <DescriptionSection description={item.description ?? undefined}/>
            <RealizationDetailSection item={item}/>
            {mapGeoJson.features.length > 0 && (
              <MapSection geoJson={mapGeoJson} bounds={mapBounds} title="Origine des bois"/>
            )}
            <ActorTransformationCard title={"Mise en oeuvre"} items={item.moes}/>
            <ActorTransformationCard title={"Réalisation"} items={item.actors}/>
          </CertificateColumn>
          {hasRightColumn && (
            <CertificateColumn>
              <RealizationCertificationLinkCertificateSection items={item.certifications}/>
              <RemovalZoneSection items={item.removalZones}/>
            </CertificateColumn>
          )}
        </CertificateBlock>
        <OuvrageRefsSection item={item}/>
        <ImageSection images={item.images} title="Photos de la réalisation"/>
        <SocialsSection realizationCertificate={item}/>
        <RealizationRefsSection id={item.id} />
      </CertificateBody>
    </CertificatePage>
  );
}
